import React from 'react';
import { Baby, AlertTriangle, Heart, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import References from '../components/References';

const Pregnancy = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1716810406166-2eee03e0bbc3?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-rose-900/80 to-pink-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Baby className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Alcohol and Pregnancy</h1>
          <p className="text-xl">Protecting your baby starts with informed choices</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Main Alert */}
        <Alert className="mb-12 bg-gradient-to-br from-red-50 to-rose-50 border-red-300">
          <ShieldAlert className="h-6 w-6 text-red-700" />
          <AlertTitle className="text-2xl font-bold text-red-900 mb-2">
            Critical Information
          </AlertTitle>
          <AlertDescription className="text-lg text-red-800">
            There is no known safe level of alcohol consumption during pregnancy. Any amount of alcohol can potentially harm your developing baby.
          </AlertDescription>
        </Alert>

        {/* Why It Matters */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <AlertTriangle className="mr-3 h-8 w-8 text-amber-600" />
              Why Alcohol and Pregnancy Don't Mix
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              When you drink alcohol during pregnancy, it passes directly from your bloodstream through the placenta to your baby. Your baby's liver is one of the last organs to develop and doesn't mature until late in pregnancy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This means your baby cannot process alcohol as well as you can, and exposure to alcohol can seriously affect their development.
            </p>
          </CardContent>
        </Card>

        {/* Risks Section */}
        <Card className="mb-12 bg-gradient-to-br from-rose-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Heart className="mr-3 h-8 w-8 text-rose-600" />
              Potential Risks to Your Baby
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-6">
              Drinking alcohol during pregnancy can lead to various complications, including:
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Fetal Alcohol Spectrum Disorder (FASD)</h3>
                <p className="text-gray-700">
                  A range of lifelong physical, behavioral, and learning disabilities that can occur in babies exposed to alcohol in the womb.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Physical Development Issues</h3>
                <p className="text-gray-700">
                  Including low birth weight, premature birth, and facial abnormalities.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Brain and Central Nervous System Problems</h3>
                <p className="text-gray-700">
                  Which can affect intellectual ability, learning, memory, and behavior throughout life.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Increased Risk of Miscarriage and Stillbirth</h3>
                <p className="text-gray-700">
                  Drinking during pregnancy increases the risk of pregnancy loss.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Our Recommendations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-900 mb-2">If you're pregnant or planning to become pregnant:</h3>
              <p className="text-gray-700">The safest choice is not to drink alcohol at all.</p>
            </div>
            
            <div className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-900 mb-2">If you're trying to conceive:</h3>
              <p className="text-gray-700">
                Avoid alcohol from the time you stop using contraception, as you may not know you're pregnant for several weeks.
              </p>
            </div>
            
            <div className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-900 mb-2">If you've already had some alcohol:</h3>
              <p className="text-gray-700">
                The best thing to do is stop drinking now. Talk to your midwife or doctor if you're concerned.
              </p>
            </div>

            <div className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-900 mb-2">Partners and whānau:</h3>
              <p className="text-gray-700">
                You can support by not drinking around the pregnant person and creating an alcohol-free environment.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="mb-12 bg-gradient-to-br from-teal-600 to-cyan-700 text-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4">Need Support?</h3>
            <p className="text-lg mb-6">
              If you're struggling with alcohol use during pregnancy, remember that help is available and it's never too late to stop drinking.
            </p>
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-xl font-bold mb-2">Alcohol Drug Helpline</p>
              <p className="text-lg">Free call: 0800 787 797</p>
              <p className="text-lg">Free text: 8681</p>
              <p className="mt-4 text-sm">Available 24/7 for free, confidential support and advice</p>
            </div>
          </CardContent>
        </Card>

        {/* References Section */}
        <References />
      </div>
    </div>
  );
};

export default Pregnancy;